import { MAP_LAYER_FACTS, MapRastersFact } from '@/api/rest/resources/types/fact';
import { FEATURE_FLAGS } from '@/config/constants';
import { useHasFeatureFlag } from '@/hooks/useHasFeatureFlag';
import { useIndicatorProduct } from '@/hooks/useIndicatorProduct';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';

import { LAYER_OPTIONS, LayerProps, MAP_ORIGIN } from '../constants';

export const useAddFactMapLayers = (layers: LayerProps[], facts: MAP_LAYER_FACTS[]): LayerProps[] => {
  const factsWithPurchasedSubscription = useGetFactsWithPurchasedSubscription(facts);
  const factsLayers = LAYER_OPTIONS.filter((layer) => facts.includes(layer.value));

  const factsWithFactOrigin = useGetFactsWithFactOrigin(factsLayers);
  const plotFacts = usePlotFacts<MapRastersFact>(factsWithFactOrigin);

  const mapFactsInLayers = layers.map((layer) => layer.value);

  const newLayers = factsLayers.filter((layer) => {
    const factName = layer.value;
    const isFactIndicatorProductSubscribed = factsWithPurchasedSubscription.includes(factName);
    const isFactAlreadyInLayers = mapFactsInLayers.includes(factName);

    if (!isFactIndicatorProductSubscribed || isFactAlreadyInLayers) {
      return false;
    }

    if (layer.origin !== MAP_ORIGIN.fact) {
      return true;
    }

    return !isNullish(getFactFromFactArray<MapRastersFact>(plotFacts, factName)?.value);
  });

  return [...layers, ...newLayers];
};

export const useAddOrgFactMapLayers = (layers: LayerProps[], facts: MAP_LAYER_FACTS[]): LayerProps[] => {
  const factsWithPurchasedSubscription = useGetFactsWithPurchasedSubscription(facts);
  const factsLayers = LAYER_OPTIONS.filter((layer) => facts.includes(layer.value));

  const factsWithFactOrigin = useGetFactsWithFactOrigin(factsLayers);
  const orgFacts = useOrganizationIndicatorReportingFact<MapRastersFact>(factsWithFactOrigin);
  const canUseOrgAggregatedRasterMaps = useHasFeatureFlag(FEATURE_FLAGS.ORG_AGGREGATED_RASTER_MAPS);
  if (!canUseOrgAggregatedRasterMaps) {
    return layers;
  }

  const mapFactsInLayers = layers.map((layer) => layer.value);

  const newLayers = factsLayers.filter((layer) => {
    const factName = layer.value;
    const isFactIndicatorProductSubscribed = factsWithPurchasedSubscription.includes(factName);
    const isFactAlreadyInLayers = mapFactsInLayers.includes(factName);

    if (!isFactIndicatorProductSubscribed || isFactAlreadyInLayers) {
      return false;
    }

    if (layer.origin !== MAP_ORIGIN.fact) {
      return true;
    }

    const factValue = orgFacts?.[`${factName}`]?.value;

    return !isNullish(factValue);
  });

  return [...layers, ...newLayers];
};

const useGetFactsWithPurchasedSubscription = (facts: MAP_LAYER_FACTS[]) => {
  const purchasedIndicatorProducts =
    useIndicatorProduct({
      purchased: true,
      visible_for_customer: true,
    }).data ?? [];

  return facts.filter((fact) => purchasedIndicatorProducts.some((ip) => ip.indicator_names.includes(fact)));
};

const useGetFactsWithFactOrigin = (factsLayers: LayerProps[]) => {
  return factsLayers.filter((layer) => layer.origin === MAP_ORIGIN.fact).map((layer) => layer.value);
};
